import { useParams } from "react-router-dom";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { useCallback, useEffect, useState, useRef } from "react";
import { isMobileSafari, isMobile } from "react-device-detect";
import PlayerBackground from "../../components/PlayerBackground";
import ScrollText from "../../components/ScrollText";
import CastButton from "../../components/CastButton";
import "./PlayerFixed.css";

// Importar funciones centralizadas
import {
  isTouchScreenDevice,
  isNewSong,
  getCover,
  createAudioElement,
  preloadStationBackground,
  updateMediaSession,
  toUpperCase,
} from "../../services/audioServices";

function PlayerFixed() {
  const { port, name } = useParams();

  // Precargar inmediatamente la imagen de fondo
  useEffect(() => {
    if (name) {
      // Precargar la imagen de fondo lo antes posible
      preloadStationBackground(name);
    }
  }, [name]);

  // Función para capitalizar la primera letra
  const capitalize = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Aplicamos capitalización al nombre de la estación
  const displayName = capitalize(name);

  let logoUrl = `https://audio.streaminghd.cl:2020/pub/${name}/background.jpg`;
  let defaultLogoUrl = `https://vod.streaminghd.cl/img/default.jpg`;

  // Estado para el reproductor
  const [audio, setAudio] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [volume, setVolume] = useState(1);
  const [mute, setMute] = useState(false);
  const [logo, setLogo] = useState(logoUrl);
  const [currentLogo, setCurrentLogo] = useState(logoUrl);
  const [fadeOut, setFadeOut] = useState(false);
  const [song, setSong] = useState("");
  const [singer, setSinger] = useState("");
  const [originalSong, setOriginalSong] = useState("");
  const [originalSinger, setOriginalSinger] = useState("");

  // Referencias
  const volumeTrackRef = useRef(null);
  const volumeHandleRef = useRef(null);
  const isDraggingRef = useRef(false);
  const volumeTimerRef = useRef(null); // Referencia para el timer de volumen
  const lastVolumeRef = useRef(1); // Guardar el último volumen antes de mutear

  const [volumeVisible, setVolumeVisible] = useState(false);

  // Detectar iOS y Safari móvil
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isIOSSafari = isIOS || isMobileSafari;

  // Efecto para manejar el cambio de logo con fade
  useEffect(() => {
    if (logo !== currentLogo) {
      // Iniciar fade out
      setFadeOut(true);

      // Después de un breve retraso, actualizar la imagen y hacer fade in
      const timer = setTimeout(() => {
        setCurrentLogo(logo);
        setFadeOut(false);
      }, 300); // 300ms para el fade out

      return () => clearTimeout(timer);
    }
  }, [logo, currentLogo]);

  // Inicializar el audio
  useEffect(() => {
    if (port && name) {
      const audioElement = createAudioElement(
        port,
        name,
        setAudio,
        setAudioError
      );
      setAudio(audioElement);

      // Limpiar al desmontar
      return () => {
        if (audioElement) {
          audioElement.pause();
          audioElement.src = "";
        }
      };
    }
  }, [port, name]);

  // Función para obtener la canción actual
  const getCurrentSong = useCallback(() => {
    isNewSong(port, singer, song).then(
      (response) => {
        getCover({
          singer: response.singer,
          song: response.song,
        }).then((cover) => {
          // Guardar versiones temporales para actualizar después
          const newSong = response.song;
          const newSinger = response.singer;
          const newOriginalSong = response.originalSong || response.song;
          const newOriginalSinger = response.originalSinger || response.singer;

          // Retraso de 2 segundos antes de cambiar la información
          setTimeout(() => {
            // Guardar versiones limpias para búsqueda futura
            setSong(newSong);
            setSinger(newSinger);

            // Guardar versiones originales con paréntesis
            setOriginalSong(newOriginalSong);
            setOriginalSinger(newOriginalSinger);

            // Actualizar el cover
            setLogo(cover);

            // Actualizar MediaSession para iOS
            updateMediaSession(
              newOriginalSong, // título
              newOriginalSinger, // artista
              displayName, // álbum (nombre de la estación)
              cover // portada
            );
          }, 2000);

          setTimeout(getCurrentSong, 10000);
        });
      },
      (error) => {
        setTimeout(getCurrentSong, 10000);
      }
    );
  }, [port, singer, song, displayName]);

  // Efecto para sincronizar el estado mute con la propiedad muted del audio
  // Específicamente útil para dispositivos iOS Safari
  useEffect(() => {
    if (audio) {
      // Establece directamente la propiedad muted del elemento de audio
      audio.muted = mute;

      // Solo para iOS Safari, como respaldo adicional
      if (isIOSSafari && mute) {
        // Uso de requestAnimationFrame para asegurar que la operación sea prioritaria
        requestAnimationFrame(() => {
          if (audio) audio.muted = true;
        });
      }
    }
  }, [mute, audio, isIOSSafari]);

  // Control de reproducción
  const onTogglePlay = () => {
    if (playing) {
      // Si está reproduciendo, pausar y eliminar
      if (audio) {
        audio.pause();
        audio.src = "";
        setAudio(null);
      }
      setPlaying(false);

      // Actualizar estado de MediaSession
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "paused";
      }
    } else {
      // Si está pausado, crear nueva conexión
      setPlaying(true);

      const streamUrl = `https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`;
      const newAudio = new Audio(streamUrl);
      newAudio.volume = volume;

      // En iOS Safari, aseguramos que el estado mute se aplique correctamente
      if (isIOSSafari && mute) {
        newAudio.muted = true;
      }

      newAudio.play().catch(() => {
        // Si falla, intentar URL alternativa
        const alternativeUrl = `http://audio.streaminghd.cl:${port}/;stream.mp3`;
        const alternativeAudio = new Audio(alternativeUrl);
        alternativeAudio.volume = volume;

        // En iOS Safari, aseguramos que el estado mute se aplique correctamente
        if (isIOSSafari && mute) {
          alternativeAudio.muted = true;
        }

        alternativeAudio.play().catch(() => {
          setPlaying(false);
          setAudioError(true);
        });

        setAudio(alternativeAudio);
      });

      setAudio(newAudio);

      // Actualizar estado de MediaSession y metadatos
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "playing";

        // Actualizar metadatos con la información actual
        updateMediaSession(originalSong, originalSinger, displayName, logo);
      }
    }
  };

  // Control de volumen
  const onToggleMute = () => {
    if (!audio) return;

    // Invertir el estado de mute
    const newMuteState = !mute;
    setMute(newMuteState);

    // Aplicar mute al audio
    audio.muted = newMuteState;

    // Si estamos muteando, guardar el volumen actual
    if (newMuteState) {
      if (volume > 0) {
        lastVolumeRef.current = volume;
      }
    } else {
      // Si estamos desmuteando y el volumen es 0, restaurar al último volumen guardado
      if (volume === 0) {
        const restoredVolume =
          lastVolumeRef.current > 0 ? lastVolumeRef.current : 0.5;
        setVolume(restoredVolume);
        audio.volume = restoredVolume;
      }
    }
  };

  // Funciones para el control de volumen
  const updateVolumeFromPosition = (clientX) => {
    if (!volumeTrackRef.current || !audio) return;

    const rect = volumeTrackRef.current.getBoundingClientRect();
    const newPosition = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const newVolume = newPosition / rect.width;

    // Guardar el último volumen conocido si es mayor que 0
    if (newVolume > 0) {
      lastVolumeRef.current = newVolume;
    }

    // Actualizar el estado y el audio
    setVolume(newVolume);
    audio.volume = newVolume;

    // Actualizar estado mute basado en el volumen
    setMute(newVolume === 0);
    audio.muted = newVolume === 0;

    // En iOS, mantener el control de volumen visible durante la interacción
    if (isIOSSafari && !volumeVisible) {
      setVolumeVisible(true);
    }
  };

  // Manejadores de eventos optimizados para volumen
  const volumeEventHandlers = {
    // Mouse events
    handleVolumeMouseDown: (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDraggingRef.current = true;
      updateVolumeFromPosition(e.clientX);

      const handleMove = (moveEvent) => {
        if (!isDraggingRef.current) return;
        moveEvent.preventDefault();
        updateVolumeFromPosition(moveEvent.clientX);
      };

      const handleUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
      };

      document.addEventListener("mousemove", handleMove, { passive: false });
      document.addEventListener("mouseup", handleUp, { once: true });
    },

    handleVolumeTrackClick: (e) => {
      e.stopPropagation();
      updateVolumeFromPosition(e.clientX);
    },

    // Touch events
    handleVolumeTouchStart: (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDraggingRef.current = true;
      updateVolumeFromPosition(e.touches[0].clientX);

      const handleTouchMove = (moveEvent) => {
        if (!isDraggingRef.current) return;
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        updateVolumeFromPosition(moveEvent.touches[0].clientX);
      };

      const handleTouchEnd = () => {
        isDraggingRef.current = false;
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { once: true });
    },
  };

  // Handlers de audio con referencias estables
  const handleAudioPause = useCallback(() => setPlaying(false), []);
  const handleAudioPlay = useCallback(() => setPlaying(true), []);
  const handleAudioEnded = useCallback(() => setPlaying(false), []);

  // Efecto para manejar eventos de audio
  useEffect(() => {
    if (!audio) return;

    audio.addEventListener("pause", handleAudioPause);
    audio.addEventListener("play", handleAudioPlay);
    audio.addEventListener("ended", handleAudioEnded);

    return () => {
      audio.removeEventListener("pause", handleAudioPause);
      audio.removeEventListener("play", handleAudioPlay);
      audio.removeEventListener("ended", handleAudioEnded);
    };
  }, [audio, handleAudioPause, handleAudioPlay, handleAudioEnded]);

  // Limpieza de event listeners del volumen
  useEffect(() => {
    return () => {
      // Limpiar el timer de volumen
      if (volumeTimerRef.current) {
        clearTimeout(volumeTimerRef.current);
      }
    };
  }, []);

  // Efecto para iniciar la obtención de datos
  useEffect(() => {
    getCurrentSong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Efecto para actualizar el título de la página cuando cambia la canción
  useEffect(() => {
    if (originalSong && originalSinger) {
      // Actualizar el título de la página con la canción y el artista
      document.title = `${originalSong} - ${originalSinger} | ${displayName}`;
    } else if (originalSong) {
      // Si solo tenemos la canción
      document.title = `${originalSong} | ${displayName}`;
    } else {
      // Si no hay información de la canción, mostrar solo el nombre de la estación
      document.title = `(Live) ${toUpperCase(displayName)}`;
    }
  }, [originalSong, originalSinger, displayName]);

  // Función para alternar la visibilidad del control de volumen en dispositivos táctiles
  const toggleVolumeVisibility = () => {
    if (isMobile) {
      // Alternar visibilidad
      setVolumeVisible(!volumeVisible);

      // Solo configuramos un timer para ocultar si estamos haciendo visible
      if (!volumeVisible) {
        // Limpiar cualquier timer existente antes de crear uno nuevo
        if (volumeTimerRef.current) {
          clearTimeout(volumeTimerRef.current);
        }

        // Configurar timer para ocultar después de un tiempo
        volumeTimerRef.current = setTimeout(() => {
          setVolumeVisible(false);
          // Importante: esto no debe afectar el estado de mute
        }, 3000); // Ocultar después de 3 segundos
      }
    }
  };

  // Renderizado del componente
  return (
    <div className="PlayerFixed">
      {/* Reemplazando el fondo con el componente centralizado */}
      <PlayerBackground
        backgroundImage={currentLogo ? currentLogo : logoUrl}
        fallbackImage={defaultLogoUrl}
        type="fixed"
        fadeOut={fadeOut}
      />

      {/* Contenido del reproductor */}
      <div className="FixedContent">
        {/* Carátula (izquierda) */}
        <div className="FixedCover">
          <img
            src={logo ? logo : logoUrl}
            alt={singer ? `${singer} - ${song}` : toUpperCase(displayName)}
            className="FixedCoverImage"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              // Si falla, usar la imagen por defecto
              currentTarget.src = defaultLogoUrl;
            }}
          />
        </div>

        {/* Información (centro-izquierda) */}
        <div className="FixedInfo">
          <ScrollText
            text={originalSong || toUpperCase(displayName)}
            className="FixedSongTitle"
            elementType="div"
          />
          {originalSinger && (
            <ScrollText
              text={originalSinger}
              className="FixedArtistName"
              elementType="div"
            />
          )}
        </div>

        {/* Animación "Al Aire" (centro) */}
        <div className="FixedOnAir">
          <div className="OnAirText">
            <span>AL</span>
            <span className="OnAirHighlight">AIRE</span>
          </div>
          <div className="OnAirDot"></div>
        </div>

        {/* Controles de volumen (derecha) */}
        <div className="FixedVolumeControl">
          <button
            className={`FixedVolumeButton ${mute ? "muted" : ""}`}
            onPointerDown={(e) => {
              // Prevenir la propagación
              e.stopPropagation();

              // Usar un manejador único para ambos tipos de dispositivos
              onToggleMute();

              // En dispositivos no táctiles, mostrar el control de volumen
              if (!isMobile && !isIOSSafari) {
                toggleVolumeVisibility();
              }
            }}
            aria-label={mute ? "Activar sonido" : "Silenciar"}
          >
            {mute ? <FaVolumeMute /> : <FaVolumeUp />}
          </button>

          <div
            className={`VolumeControlContainer ${
              (isMobile || isIOSSafari) &&
              volumeVisible &&
              !isTouchScreenDevice()
                ? "VolumeControlContainerMobile"
                : ""
            }`}
          >
            <div
              className="VolumeTrack"
              ref={volumeTrackRef}
              onClick={volumeEventHandlers.handleVolumeTrackClick}
              onTouchStart={(e) => {
                if (isIOSSafari) {
                  e.preventDefault();
                  updateVolumeFromPosition(e.touches[0].clientX);
                }
              }}
            >
              <div
                className="VolumeFill"
                style={{
                  width: `${mute ? 0 : volume * 100}%`,
                }}
              />
              <div
                className="VolumeHandle"
                ref={volumeHandleRef}
                style={{
                  left: `${mute ? 0 : volume * 100}%`,
                  opacity: mute ? 0.5 : 1,
                }}
                onMouseDown={volumeEventHandlers.handleVolumeMouseDown}
                onTouchStart={volumeEventHandlers.handleVolumeTouchStart}
                aria-label="Ajustar volumen"
                role="slider"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(mute ? 0 : volume * 100)}
              />
            </div>
          </div>
        </div>

        {/* Control de reproducción (extremo derecho) */}
        <div className="FixedControls">
          <button
            className="FixedPlayButton"
            onClick={onTogglePlay}
            aria-label={playing ? "Pausar" : "Reproducir"}
            disabled={audioError}
          >
            {playing ? <FaPause /> : <FaPlay />}
          </button>

          {/* Botón de Cast/AirPlay */}
          <CastButton
            streamUrl={`https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`}
            title={originalSong || toUpperCase(displayName)}
            artist={originalSinger || ""}
            album={toUpperCase(displayName)}
            image={logo || logoUrl}
            className="FixedCastButton"
            audio={audio}
          />
        </div>
      </div>
    </div>
  );
}

export default PlayerFixed;
